import { useState, useEffect } from "react";
import { api, API, apiErr } from "@/lib/api";
import { useFetch } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Empty, PageHeader } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BookOpen, Eye, ShieldCheck, Maximize2, X, FileCode, CheckCircle2, PlayCircle, AlertTriangle, Download } from "lucide-react";
import { ytEmbed, isVideoFile } from "@/lib/media";
import { MediaThumb } from "@/components/MediaThumb";
import { downloadForOffline, cacheForOffline, downloadable, offlineSet } from "@/lib/offline";

export default function LmsLibrary() {
  const { L } = useLang();
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const { data: docs, loading, reload } = useFetch(() => api.get("/lms/documents"));
  const { data: kids } = useFetch(() => isParent ? api.get("/students") : Promise.resolve([]), [isParent]);
  const [viewing, setViewing] = useState(null);
  const [fullscreen, setFullscreen] = useState(null);
  const [child, setChild] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [watchTarget, setWatchTarget] = useState(null);
  const [watchChild, setWatchChild] = useState("");
  const [frameError, setFrameError] = useState({});
  const [fsError, setFsError] = useState(false);
  const [offline, setOffline] = useState(() => offlineSet());
  const [dlBusy, setDlBusy] = useState("");
  const canOffline = !!user && user.role !== "parent";
  useEffect(() => {
    const h = () => setOffline(offlineSet());
    window.addEventListener("lms-offline-changed", h);
    return () => window.removeEventListener("lms-offline-changed", h);
  }, []);
  const saveOffline = async (d) => {
    setDlBusy(d.id);
    try { await downloadForOffline(d); toast.success(L("Saved for offline reading ✓", "Enregistré pour lecture hors-ligne ✓")); }
    catch { toast.error(L("Download failed — please try again while online", "Échec du téléchargement — réessayez en ligne")); }
    finally { setDlBusy(""); }
  };
  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;

  const allSubjects = [...new Set((docs || []).map((d) => d.subject).filter(Boolean))];
  const shown = (docs || []).filter((d) => subjectFilter === "all" || d.subject === subjectFilter);
  const grades = {};
  shown.forEach((d) => { (grades[d.grade_level] = grades[d.grade_level] || []).push(d); });
  const gradeKeys = Object.keys(grades);
  const fsDoc = (docs || []).find((d) => d.id === fullscreen);
  const fsSrc = fsDoc ? (fsDoc.is_package ? `${API}/lms/pkg/${fsDoc.id}/${fsDoc.entry}` : `${API}/lms/documents/${fsDoc.id}/view`) : "";
  const fsChildren = fsDoc ? ((kids || []).filter((k) => k.status === "accepted" && k.grade_level === fsDoc.grade_level)) : [];

  const markComplete = async () => {
    if (!fsDoc) return;
    const payload = {};
    if (isParent) {
      if (fsChildren.length > 1 && !child) { toast.error(L("Choose which child", "Choisissez l'enfant")); return; }
      payload.student_id = child || (fsChildren[0]?.id);
    }
    try {
      await api.post(`/lms/documents/${fsDoc.id}/complete`, payload);
      toast.success(L("Marked as complete ✓", "Marqué comme terminé ✓"));
      reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const markWatched = async (d, studentId) => {
    try {
      await api.post(`/lms/documents/${d.id}/complete`, isParent ? { student_id: studentId } : {});
      toast.success(L("Marked as watched ✓", "Marqué comme vu ✓")); setWatchTarget(null); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const onWatchClick = (d) => {
    const ch = (kids || []).filter((k) => k.status === "accepted" && k.grade_level === d.grade_level);
    if (isParent && ch.length > 1) { setWatchTarget(d.id); setWatchChild(""); }
    else markWatched(d, isParent ? ch[0]?.id : undefined);
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title={L("Library", "Bibliothèque")}
        description={canOffline
          ? L("Course materials. Open a document to read it — or download it for offline reading anytime.", "Supports de cours. Ouvrez un document pour le lire — ou téléchargez-le pour une lecture hors-ligne à tout moment.")
          : L("Course materials for your child's grade. View-only — download and print are disabled.", "Supports de cours pour le niveau de votre enfant. Lecture seule — téléchargement et impression désactivés.")} />
      {allSubjects.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="library-subject-filter">
          {["all", ...allSubjects].map((s) => (
            <button key={s} onClick={() => setSubjectFilter(s)} data-testid={`library-subject-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${subjectFilter === s ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
              {s === "all" ? L("All subjects", "Toutes les matières") : s}
            </button>
          ))}
        </div>
      )}
      {gradeKeys.length === 0 ? <Empty title={L("No materials yet", "Aucun support pour l'instant")} /> : (
        <div className="space-y-6">
          {gradeKeys.map((g) => (
            <div key={g} data-testid={`library-grade-${g}`}>
              <p className="font-heading font-bold text-lg mb-2 flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" />{g}</p>
              <div className="space-y-3">
                {grades[g].map((d) => (
                  <div key={d.id} className="bg-card border border-border rounded-lg" data-testid={`library-doc-${d.id}`}>
                    <div className="p-4 flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-medium flex items-center gap-2">
                        {d.kind === "link" && <MediaThumb url={d.url} className="h-10 w-16" onClick={() => setViewing(viewing === d.id ? null : d.id)} testId={`library-thumb-${d.id}`} />}
                        {d.kind === "html" ? <FileCode className="h-4 w-4 text-accent" /> : null}{d.title}
                        {d.subject && <Badge variant="secondary" className="text-xs">{d.subject}</Badge>}
                        {d.kind === "html" && <span className="text-xs bg-accent/15 text-accent rounded px-2 py-0.5">{L("Interactive", "Interactif")}</span>}
                        {d.kind === "link" && <span className="text-xs bg-accent/15 text-accent rounded px-2 py-0.5">{L("Video", "Vidéo")}</span>}
                        {d.completed && <span className="text-xs bg-emerald-100 text-emerald-700 rounded px-2 py-0.5 flex items-center gap-1" data-testid={`library-done-${d.id}`}><CheckCircle2 className="h-3 w-3" />{L("Completed", "Terminé")}</span>}
                        {canOffline && offline.has(d.id) && <span className="text-xs bg-sky-100 text-sky-700 rounded px-2 py-0.5 flex items-center gap-1" data-testid={`library-offline-${d.id}`}><Download className="h-3 w-3" />{L("Available offline", "Disponible hors-ligne")}</span>}
                      </span>
                      {d.kind === "link" ? (
                        <div className="flex items-center gap-2">
                          <Button size="sm" onClick={() => setViewing(viewing === d.id ? null : d.id)} data-testid={`library-play-${d.id}`}>
                            <PlayCircle className="h-4 w-4 mr-1" />{viewing === d.id ? L("Hide", "Masquer") : L("Play video", "Lire la vidéo")}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => onWatchClick(d)} data-testid={`library-watch-${d.id}`}>
                            <CheckCircle2 className="h-4 w-4 mr-1" />{d.completed ? L("Watched", "Vu") : L("Mark watched", "Marquer vu")}
                          </Button>
                        </div>
                      ) : d.kind === "html" ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button size="sm" onClick={() => { setFullscreen(d.id); if (canOffline && downloadable(d)) cacheForOffline(d); }} data-testid={`library-open-${d.id}`}>
                            <Maximize2 className="h-4 w-4 mr-1" />{L("Open module", "Ouvrir le module")}
                          </Button>
                          {canOffline && downloadable(d) && (
                            <Button size="sm" variant="outline" disabled={dlBusy === d.id} onClick={() => saveOffline(d)} data-testid={`library-download-${d.id}`}>
                              <Download className="h-4 w-4 mr-1" />{dlBusy === d.id ? L("Saving…", "…") : L("Download offline", "Télécharger hors-ligne")}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => { const opening = viewing !== d.id; setViewing(opening ? d.id : null); if (opening && canOffline && downloadable(d)) cacheForOffline(d); }} data-testid={`library-view-${d.id}`}>
                            <Eye className="h-4 w-4 mr-1" />{viewing === d.id ? L("Hide", "Masquer") : L("Read", "Lire")}
                          </Button>
                          {canOffline && downloadable(d) && (
                            <Button size="sm" variant="ghost" disabled={dlBusy === d.id} onClick={() => saveOffline(d)} data-testid={`library-download-${d.id}`}>
                              <Download className="h-4 w-4 mr-1" />{dlBusy === d.id ? L("Saving…", "…") : L("Download offline", "Télécharger hors-ligne")}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    {watchTarget === d.id && (
                      <div className="border-t border-border p-3 flex items-center gap-2 flex-wrap" data-testid={`library-watch-confirm-${d.id}`}>
                        <Select value={watchChild} onValueChange={setWatchChild}>
                          <SelectTrigger className="w-56" data-testid={`library-watch-child-${d.id}`}><SelectValue placeholder={L("Select child", "Choisir l'enfant")} /></SelectTrigger>
                          <SelectContent>{(kids || []).filter((k) => k.status === "accepted" && k.grade_level === d.grade_level).map((k) => <SelectItem key={k.id} value={k.id}>{k.first_name} {k.last_name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Button size="sm" onClick={() => { if (!watchChild) { toast.error(L("Choose which child", "Choisissez l'enfant")); return; } markWatched(d, watchChild); }} data-testid={`library-watch-go-${d.id}`}>{L("Confirm", "Confirmer")}</Button>
                        <Button size="sm" variant="ghost" onClick={() => setWatchTarget(null)}>{L("Cancel", "Annuler")}</Button>
                      </div>
                    )}
                    {viewing === d.id && d.kind === "link" && (
                      <div className="border-t border-border p-3">
                        <div className="relative w-full bg-black rounded overflow-hidden" style={{ height: "60vh" }}>
                          {ytEmbed(d.url)
                            ? <iframe title={d.title} src={ytEmbed(d.url)} className="w-full h-full border-0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid={`library-yt-${d.id}`} />
                            : isVideoFile(d.url)
                              ? <video src={d.url} controls className="w-full h-full" data-testid={`library-video-${d.id}`} />
                              : <div className="flex items-center justify-center h-full"><a href={d.url} target="_blank" rel="noreferrer" className="text-white underline" data-testid={`library-extlink-${d.id}`}>{L("Open the link", "Ouvrir le lien")}</a></div>}
                        </div>
                      </div>
                    )}
                    {viewing === d.id && d.kind !== "html" && d.kind !== "link" && (
                      <div className="border-t border-border p-3" onContextMenu={(e) => e.preventDefault()}>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                          <ShieldCheck className="h-4 w-4" />{canOffline ? L("You can download this document for offline reading.", "Vous pouvez télécharger ce document pour une lecture hors-ligne.") : L("View-only. Downloading and printing are disabled.", "Lecture seule. Téléchargement et impression désactivés.")}
                        </div>
                        <div className="relative w-full" style={{ height: "72vh" }}>
                          {frameError[d.id] ? (
                            <div className="w-full h-full border border-border rounded flex flex-col items-center justify-center text-center gap-2 p-6 bg-secondary/40" data-testid={`library-frame-error-${d.id}`}>
                              <AlertTriangle className="h-8 w-8 text-amber-500" />
                              <p className="font-semibold">{L("This document could not be loaded.", "Ce document n'a pas pu être chargé.")}</p>
                              <p className="text-xs text-muted-foreground max-w-sm">{L("The file may be missing or temporarily unavailable. Please retry, or contact the school office.", "Le fichier est peut-être manquant ou temporairement indisponible. Réessayez, ou contactez le secrétariat.")}</p>
                              <Button size="sm" variant="outline" onClick={() => setFrameError((p) => ({ ...p, [d.id]: false }))} data-testid={`library-frame-retry-${d.id}`}>{L("Retry", "Réessayer")}</Button>
                            </div>
                          ) : (
                            <iframe title={d.title} src={`${API}/lms/documents/${d.id}/view#toolbar=0&navpanes=0&scrollbar=0`} className="w-full h-full border border-border rounded" data-testid={`library-frame-${d.id}`} onError={() => setFrameError((p) => ({ ...p, [d.id]: true }))} />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {fsDoc && (
        <div className="fixed inset-0 z-[60] bg-background flex flex-col" data-testid="library-fullscreen">
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-border bg-card">
            <span className="font-heading font-bold truncate">{fsDoc.title}</span>
            <Button size="sm" variant="outline" onClick={() => { setFsError(false); setFullscreen(null); }} data-testid="library-fullscreen-close"><X className="h-4 w-4 mr-1" />{L("Close", "Fermer")}</Button>
          </div>
          {fsError ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 p-8" data-testid="library-fullscreen-error">
              <AlertTriangle className="h-10 w-10 text-amber-500" />
              <p className="font-semibold text-lg">{L("This document could not be loaded.", "Ce document n'a pas pu être chargé.")}</p>
              <p className="text-sm text-muted-foreground max-w-md">{L("The file may be missing or temporarily unavailable. Please retry, or contact the school office.", "Le fichier est peut-être manquant ou temporairement indisponible. Réessayez, ou contactez le secrétariat.")}</p>
              <Button variant="outline" onClick={() => setFsError(false)} data-testid="library-fullscreen-retry">{L("Retry", "Réessayer")}</Button>
            </div>
          ) : (
            <iframe
              key={fsDoc.id}
              title={fsDoc.title}
              src={fsSrc}
              className="flex-1 w-full border-0"
              sandbox="allow-scripts allow-forms allow-popups allow-modals allow-pointer-lock allow-downloads"
              data-testid="library-fullscreen-frame"
              onError={() => setFsError(true)}
            />
          )}
          <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-border bg-card flex-wrap">
            {isParent && fsChildren.length > 1 && (
              <Select value={child} onValueChange={setChild}>
                <SelectTrigger className="w-56" data-testid="library-complete-child"><SelectValue placeholder={L("Select child", "Choisir l'enfant")} /></SelectTrigger>
                <SelectContent>{fsChildren.map((k) => <SelectItem key={k.id} value={k.id}>{k.first_name} {k.last_name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={markComplete} data-testid="library-mark-complete">
              <CheckCircle2 className="h-4 w-4 mr-1" />{L("Mark as Complete", "Marquer comme terminé")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
